import { createApi } from '@reduxjs/toolkit/query/react';
import { Paginated } from '@/lib/interfaces/paginated.interface';
import { Organization } from '@/lib/models/organization.model';
import { OrganizationSpecialist } from '@/lib/models/organization-specialist.model';

import { ApiTag } from '../api-tags.enum';
import { HttpMethod } from '../http-method.enum';
import { CreateOrganization } from './interfaces/create-organization.interface';
import { UpdateOrganization } from './interfaces/update-organization.interface';
import { OrganizationStatistics } from '@/lib/models/organization-statistics.model';
import { patientsApi } from '../patients/patients.api';
import { specialistsApi } from '../specialists/specialists.api';
import { apiBaseQuery } from '../base-query';

export const organizationsApi = createApi({
  reducerPath: 'organizationsApi',
  tagTypes: [ApiTag.Organization, ApiTag.OrganizationSpecialist],
  refetchOnReconnect: true,
  refetchOnFocus: true,
  baseQuery: apiBaseQuery,
  endpoints: (builder) => ({
    createOrganization: builder.mutation<Organization, CreateOrganization>({
      query: (createDto) => ({
        url: `/organizations`,
        method: HttpMethod.Post,
        body: createDto,
      }),
      invalidatesTags: (result) => {
        return result ? [ApiTag.Organization] : [];
      },
    }),
    getAllOrganizations: builder.query<
      Paginated<Organization[]>,
      { includeDeleted?: boolean } | void
    >({
      query: (params) => ({
        url: `/organizations`,
        method: HttpMethod.Get,
        params: params || {},
      }),
      providesTags: (result) =>
        result
          ? [
              ApiTag.Organization,
              ...result.data.map(({ id }) => ({
                type: ApiTag.Organization as const,
                id,
              })),
            ]
          : [ApiTag.Organization],
    }),
    getOrganizationById: builder.query<Organization, Organization['id']>({
      query: (id: Organization['id']) => `/organizations/${id}`,
      providesTags: (result, error, id) =>
        result ? [{ type: ApiTag.Organization, id }] : [ApiTag.Organization],
    }),
    updateOrganization: builder.mutation<
      Organization,
      Partial<Organization> & Pick<Organization, 'id'>
    >({
      query: ({ id, ...updates }) => {
        const body: UpdateOrganization = {
          title: updates.title,
          contactEmail: updates.contactEmail,
          contactName: updates.contactName,
        };

        return {
          url: `/organizations/${id}`,
          method: HttpMethod.Patch,
          body,
        };
      },
      invalidatesTags: (result, error, { id }) => {
        return result ? [{ type: ApiTag.Organization, id }] : [];
      },
    }),
    getOrganizationSpecialists: builder.query<
      OrganizationSpecialist[],
      Organization['id']
    >({
      query: (id: Organization['id']) => `/organizations/${id}/specialists`,
      providesTags: (result) =>
        result
          ? [
              ApiTag.OrganizationSpecialist,
              ...result.map(({ id }) => ({
                type: ApiTag.Organization as const,
                id,
              })),
            ]
          : [ApiTag.OrganizationSpecialist],
    }),
    getOrganizationStatistics: builder.query<
      OrganizationStatistics,
      Organization['id']
    >({
      query: (id: Organization['id']) =>
        `/organizations/${id}/statistics?date=${new Date().toISOString()}`,
      providesTags: (result, error, id) =>
        result ? [{ type: ApiTag.Organization, id }] : [ApiTag.Organization],
    }),
    exportOrganizationStatistics: builder.query<File, void>({
      query: () => ({
        url: `/organizations/statistics/export?type=csv`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const filename = response.headers
            .get('content-disposition')
            ?.split('filename=')[1];

          return new File([blob], filename ?? 'organization-statistics.csv');
        },
      }),
    }),
    deleteOrganization: builder.mutation<number, Organization['id']>({
      query: (id) => ({
        url: `/organizations/${id}`,
        method: HttpMethod.Delete,
      }),
      invalidatesTags: (result, error, id) => [
        ApiTag.Organization,
        { type: ApiTag.Organization, id },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(patientsApi.util.invalidateTags([ApiTag.Patient]));
        dispatch(specialistsApi.util.invalidateTags([ApiTag.Specialist]));
      },
    }),
    restoreOrganization: builder.mutation<number, Organization['id']>({
      query: (id) => ({
        url: `/organizations/${id}/restore`,
        method: HttpMethod.Patch,
      }),
      invalidatesTags: (result, error, id) => [
        ApiTag.Organization,
        { type: ApiTag.Organization, id },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(patientsApi.util.invalidateTags([ApiTag.Patient]));
        dispatch(specialistsApi.util.invalidateTags([ApiTag.Specialist]));
      },
    }),
  }),
});

export const {
  useCreateOrganizationMutation,
  useGetAllOrganizationsQuery,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationMutation,
  useGetOrganizationSpecialistsQuery,
  useGetOrganizationStatisticsQuery,
  useLazyExportOrganizationStatisticsQuery,
  useDeleteOrganizationMutation,
  useRestoreOrganizationMutation,
} = organizationsApi;
